import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Car, 
  Users, 
  Shield, 
  Clock,
  MapPin,
  Wifi,
  Snowflake,
  Music,
  Star,
  CheckCircle,
  Plane
} from "lucide-react";
import { useTranslatedText } from "@/hooks/useTranslatedText";
import { useVehicleImages } from "@/hooks/useVehicleImages";
import transfersHeroBg from "@/assets/transfers-hero-bg.jpg";

const Transfers = () => {
  const { translatedText: bookYourTransferTodayText } = useTranslatedText("Book Your Transfer Today");
  const { translatedText: transferCtaDescriptionText } = useTranslatedText("Secure, comfortable, and reliable transportation is just a booking away. Contact us for instant quotes and reservations.");
  
  const { vehicleImages, loading } = useVehicleImages();

  const transferTypes = [
    {
      title: "Airport Transfers",
      description: "Reliable pickup and drop-off service from/to Punta Cana International Airport",
      icon: Plane,
      routes: [
        "Airport to Bavaro/Punta Cana Hotels",
        "Airport to Cap Cana Resorts",
        "Airport to Uvero Alto Hotels",
        "Airport to La Romana"
      ]
    },
    {
      title: "Hotel Transfers",
      description: "Comfortable transportation between hotels and destinations",
      icon: MapPin,
      routes: [
        "Hotel to Hotel Transfers",
        "Hotel to Shopping Centers",
        "Hotel to Restaurants",
        "Hotel to Attractions"
      ]
    },
    {
      title: "Private Transportation",
      description: "Dedicated vehicle and driver for your exclusive use",
      icon: Car,
      routes: [
        "Custom Itineraries",
        "Multiple Destinations",
        "Wait Time Included",
        "Flexible Scheduling"
      ]
    },
    {
      title: "Group Transportation",
      description: "Specialized service for large groups and corporate events",
      icon: Users,
      routes: [
        "Wedding Groups",
        "Corporate Events",
        "Family Reunions",
        "Tour Groups"
      ]
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Safety First",
      description: "All vehicles regularly inspected and fully insured"
    },
    {
      icon: Clock,
      title: "On-Time Service",
      description: "Punctual pickup and delivery with flight monitoring"
    },
    {
      icon: Users,
      title: "Professional Drivers",
      description: "Licensed, experienced drivers with local knowledge"
    },
    {
      icon: Wifi,
      title: "Modern Amenities",
      description: "Air conditioning, comfortable seating, and modern features"
    }
  ];

  const popularRoutes = [
    {
      route: "Airport ↔ Bavaro/Punta Cana",
      time: "45 minutes",
      distance: "25 km",
      price: "From $35"
    },
    {
      route: "Airport ↔ Cap Cana",
      time: "30 minutes",
      distance: "15 km",
      price: "From $40"
    },
    {
      route: "Airport ↔ Uvero Alto",
      time: "60 minutes",
      distance: "35 km",
      price: "From $45"
    },
    {
      route: "Airport ↔ La Romana",
      time: "90 minutes",
      distance: "60 km",
      price: "From $65"
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section 
        className="py-20 text-white relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${transfersHeroBg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">Transfer Services</Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Reliable Transportation in Punta Cana
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Professional transfer services for individuals, families, and groups. 
              From airport pickups to custom transportation solutions, we ensure 
              your journey is comfortable, safe, and punctual.
            </p>
          </div>
        </div>
      </section>

      {/* Vehicle Fleet */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-primary">Our Fleet</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Choose Your Perfect Vehicle
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From intimate couples' transfers to large group transportation, 
              we have the perfect vehicle for every need and budget
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading vehicles...</p>
            </div>
          ) : vehicleImages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No vehicles available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {vehicleImages
                .sort((a, b) => a.min_passengers - b.min_passengers)
                .map((vehicle, index) => (
                <Card key={vehicle.id} className={`text-center hover:shadow-primary transition-all duration-300 hover:-translate-y-2 border-0 shadow-md relative ${index === 1 ? 'ring-2 ring-primary' : ''}`}>
                  {index === 1 && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground">
                      Most Popular
                    </Badge>
                  )}
                  <CardHeader>
                    <div 
                      className="w-full h-32 bg-cover bg-center bg-no-repeat rounded-lg mb-4"
                      style={{ backgroundImage: `url(${vehicle.image_url})` }}
                    >
                      <div className="w-full h-full bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
                    </div>
                    <CardTitle className="text-xl">{vehicle.vehicle_name}</CardTitle>
                    <Badge className="mt-2 bg-gradient-primary text-white">
                      {vehicle.service_type === 'standard' ? 'Standard Service' : 'VIP Service'}
                    </Badge>
                    <CardDescription className="mt-2">
                      {vehicle.description || 'Professional transfer service'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Capacity:</span>
                        <span className="font-medium">{vehicle.min_passengers}-{vehicle.max_passengers} passengers</span>
                      </div>
                    </div>
                    <div className="space-y-2 mb-6">
                      {vehicle.features && vehicle.features.length > 0 ? (
                        vehicle.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center space-x-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-muted-foreground">{feature}</span>
                          </div>
                        ))
                      ) : (
                        <div className="flex items-center space-x-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-muted-foreground">Professional Service</span>
                        </div>
                      )}
                    </div>
                    <Button className={`w-full ${index === 1 ? 'bg-gradient-primary shadow-primary' : 'bg-gradient-secondary shadow-secondary'} hover:shadow-primary transition-all duration-300`}>
                      Book Now
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Transfer Types */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-secondary">Service Types</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Comprehensive Transfer Solutions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Whether you need a simple airport pickup or complex multi-destination 
              transportation, we have the perfect solution for you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {transferTypes.map((type, index) => (
              <Card key={index} className="hover:shadow-primary transition-all duration-300 hover:-translate-y-2 border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                      <type.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{type.title}</CardTitle>
                      <CardDescription className="text-base">
                        {type.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {type.routes.map((route, routeIndex) => (
                      <div key={routeIndex} className="flex items-center space-x-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                        <span className="text-muted-foreground">{route}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-primary">Popular Routes</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Most Requested Transfer Routes
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our most popular transfer destinations with estimated times and distances
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularRoutes.map((route, index) => (
              <Card key={index} className="text-center hover:shadow-primary transition-all duration-300 hover:-translate-y-2 border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg">{route.route}</CardTitle>
                  <div className="text-2xl font-bold text-primary">{route.price}</div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="font-medium">{route.time}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Distance:</span>
                      <span className="font-medium">{route.distance}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-secondary">Why Choose Our Transfers</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Premium Service Standards
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every aspect of our service is designed to provide you with the 
              best possible transportation experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-primary transition-all duration-300 hover:-translate-y-2 border-0 shadow-md">
                <CardHeader>
                  <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mb-4">
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-hero rounded-2xl p-8 md:p-12 text-white text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {bookYourTransferTodayText}
              </h2>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                {transferCtaDescriptionText}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="flex items-center justify-center space-x-3">
                  <Clock className="h-6 w-6" />
                  <span>24/7 Availability</span>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <Shield className="h-6 w-6" />
                  <span>Fully Insured</span>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <Star className="h-6 w-6" />
                  <span>5-Star Service</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6">
                  Get Instant Quote
                </Button>
                <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm text-lg px-8 py-6">
                  Call Now: +1 (809) 123-4567
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Transfers;